// src/Components/Common/ProgressContainer.tsx

import React, { useEffect, useRef } from "react";
import styled, { keyframes } from "styled-components";
import { MOBILE_MAX_WIDTH, BREAKPOINTS } from "../styles/const_style";

const Container = styled.div`
    position: fixed;
    top: 70px;
    right: 20px;
    width: 300px;
    max-height: 400px;
    overflow-y: auto;
    background-color: var(--card-bg-color);
    color: var(--text-color);
    border: 1px solid var(--gray-color);
    border-radius: 8px;
    padding: 10px;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
    z-index: 1000;

    @media screen and (max-width: ${MOBILE_MAX_WIDTH}) {
        right: 10px;
        left: 10px;
        width: auto;
        max-height: 350px;
        top: 60px;
    }

    @media screen and (max-width: ${BREAKPOINTS.MOBILE}) {
        max-height: 300px;
        padding: 8px;
        top: 50px;
    }
`;

const LogEntry = styled.div<{ status: string }>`
    padding: 5px;
    margin-bottom: 5px;
    border-left: 3px solid ${props =>
        props.status === 'success' ? '#28a745' :
        props.status === 'error' ? '#dc3545' :
        props.status === 'warning' ? '#ffc107' : '#17a2b8'};
    font-size: 12px;

    @media screen and (max-width: ${BREAKPOINTS.MOBILE}) {
        font-size: 11px;
        padding: 4px;
    }
`;

const TimeStamp = styled.span`
    color: #666;
    margin-right: 8px;
    font-size: 10px;
`;

const Message = styled.span`
    color: var(--text-color);
`;

const SongsList = styled.ul`
    list-style-type: none;
    padding-left: 10px;
    margin-top: 10px;
`;

const SongItem = styled.li`
    padding: 3px 0;
    font-size: 12px;
`;

const CloseButton = styled.button`
    position: absolute;
    top: 5px;
    right: 5px;
    background: none;
    border: none;
    cursor: pointer;
    font-size: 16px;
    color: var(--text-color);
    opacity: 0.6;

    &:hover {
        opacity: 1;
    }
`;

const progressAnimation = keyframes`
    from {
        width: 0;
    }
    to {
        width: 100%;
    }
`;

const ProgressBar = styled.div`
    position: absolute;
    bottom: 0;
    left: 0;
    height: 4px;
    background-color: red;
    animation: ${progressAnimation} 2s linear;
`;

interface LogMessage {
    status: string;
    message: string;
    timestamp: string;
}

interface UploadedSong {
    artist: string;
    title: string;
    filename: string;
}

interface ProgressContainerProps {
    messages: LogMessage[];
    uploadedSongs: UploadedSong[];
    isRefreshing: boolean;
    showProgressBar: boolean;
    onClose: () => void;
}

const ProgressContainer: React.FC<ProgressContainerProps> = ({
    messages,
    uploadedSongs,
    isRefreshing,
    showProgressBar,
    onClose
}) => {
    const progressContainerRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (progressContainerRef.current) {
            progressContainerRef.current.scrollTop = progressContainerRef.current.scrollHeight;
        }
    }, [messages]);

    return (
        <Container ref={progressContainerRef}>
            <CloseButton onClick={onClose}>×</CloseButton>
            <h4 style={{ margin: '0 0 10px 0' }}>새로고침 진행 상황</h4>
            
            <div>
                {messages.map((msg, idx) => (
                    <LogEntry key={idx} status={msg.status}>
                        <TimeStamp>{msg.timestamp}</TimeStamp>
                        <Message>{msg.message}</Message>
                    </LogEntry>
                ))}
            </div>
            
            {uploadedSongs.length > 0 && (
                <div>
                    <h5 style={{ margin: '10px 0 5px 0' }}>업로드된 노래 ({uploadedSongs.length})</h5>
                    <SongsList>
                        {uploadedSongs.map((song, idx) => (
                            <SongItem key={idx}>
                                {song.artist} - {song.title}
                            </SongItem>
                        ))}
                    </SongsList>
                </div>
            )}
            
            {isRefreshing && (
                <div style={{ textAlign: 'center', margin: '10px 0' }}>
                    <span style={{ fontSize: '12px', color: '#666' }}>처리 중입니다...</span>
                </div>
            )}

            {showProgressBar && <ProgressBar />}
        </Container>
    );
};

export default ProgressContainer;